import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  Ref,
  SelectHTMLAttributes,
} from 'react';
import { useId } from 'react';

/* ── Icon ──────────────────────────────────────────────────────────────────
   Phosphor, loaded once as a stylesheet by each app. Wrapped so the class
   convention lives in one place and every icon is hidden from assistive tech
   unless it is given a label — an unlabelled decorative glyph read aloud is
   noise, and a meaningful one with no label is lost information. */
export function Icon({
  name,
  label,
  className,
  style,
}: {
  /** Phosphor name without the `ph-` prefix, e.g. "gauge". */
  name: string;
  label?: string | undefined;
  className?: string | undefined;
  style?: React.CSSProperties | undefined;
}) {
  const cls = ['ph', `ph-${name}`, className].filter(Boolean).join(' ');
  return label ? (
    <i className={cls} role="img" aria-label={label} style={style} />
  ) : (
    <i className={cls} aria-hidden="true" style={style} />
  );
}

/* ── Button ─────────────────────────────────────────────────────────────── */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Declared because React 19 only accepts `ref` on a function component when
   * its props say so — `ButtonHTMLAttributes` does not carry one. Without this
   * line a caller cannot focus a Button, which is what returning focus to a
   * disclosure trigger requires. `Input` above does exactly this already; this
   * is that precedent applied, not a new pattern.
   */
  ref?: Ref<HTMLButtonElement>;
  variant?: ButtonVariant;
  /** Square 36px icon-only button. Requires aria-label. */
  iconOnly?: boolean;
  block?: boolean;
  small?: boolean;
  /** Renders a spinner and blocks repeat submits. */
  busy?: boolean;
}

export function Button({
  variant = 'secondary',
  iconOnly = false,
  block = false,
  small = false,
  busy = false,
  className,
  children,
  disabled,
  ref,
  type = 'button',
  ...rest
}: ButtonProps) {
  const cls = [
    'btn',
    `btn-${variant}`,
    iconOnly && 'btn-icon',
    block && 'btn-block',
    small && 'btn-sm',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button
      ref={ref}
      type={type}
      className={cls}
      disabled={disabled ?? busy}
      aria-busy={busy}
      {...rest}
    >
      {busy ? <Icon name="circle-notch" className="spin" /> : null}
      {children}
    </button>
  );
}

/* ── Tag ────────────────────────────────────────────────────────────────── */
/** `warn` is the attention tone `.notice-warn` is: something a person must look at, never a result. */
export type TagTone = 'accent' | 'neutral' | 'outline' | 'quiet' | 'warn';

export function Tag({
  tone = 'neutral',
  children,
  className,
}: {
  tone?: TagTone;
  children: ReactNode;
  className?: string;
}) {
  return <span className={['tag', `tag-${tone}`, className].filter(Boolean).join(' ')}>{children}</span>;
}

/* ── Field + controls ───────────────────────────────────────────────────── */
export interface FieldProps {
  label: string;
  /** Validation message. Field-level problems stay inline; failures open the dialog. */
  error?: string | undefined;
  hint?: string | undefined;
  children: (props: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
}

export function Field({ label, error, hint, children }: FieldProps) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ') || undefined;
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children({ id, describedBy, invalid: Boolean(error) })}
      {hint ? (
        <span id={hintId} className="field-hint">
          {hint}
        </span>
      ) : null}
      {error ? (
        <span id={errorId} className="field-error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  ref?: Ref<HTMLInputElement>;
}

export function Input({ className, ref, ...rest }: InputProps) {
  return <input ref={ref} className={['input', className].filter(Boolean).join(' ')} {...rest} />;
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={['input', className].filter(Boolean).join(' ')} {...rest}>
      {children}
    </select>
  );
}

/** Checkbox and radio share one control; only the corner differs. */
export function Choice({
  type = 'checkbox',
  label,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  return (
    <label className="radio">
      <input type={type} {...rest} />
      <span className={type === 'checkbox' ? 'dot square' : 'dot'} />
      {label}
    </label>
  );
}

/* ── Card / Panel / Stat ────────────────────────────────────────────────── */
export function Panel({
  title,
  actions,
  children,
  className,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={['panel', className].filter(Boolean).join(' ')}>
      {title || actions ? (
        <div className="panel-head">
          {typeof title === 'string' ? <h3 className="panel-title">{title}</h3> : title}
          {actions ? <div className="header-spacer">{actions}</div> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export function Stat({
  label,
  value,
  delta,
  deltaUp = false,
  large = false,
  children,
}: {
  label: string;
  value: ReactNode;
  delta?: ReactNode;
  deltaUp?: boolean;
  large?: boolean;
  children?: ReactNode;
}) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className={large ? 'stat-value lg' : 'stat-value'}>{value}</div>
      {delta ? <div className={deltaUp ? 'stat-delta up' : 'stat-delta'}>{delta}</div> : null}
      {children}
    </div>
  );
}

/* ── EmptyState ─────────────────────────────────────────────────────────────
   What a surface with no data source renders. It says what is missing and why,
   and never a fabricated zero — a "₱0" that means "not built yet" is a lie
   an operator will act on.

   THE BODY IS A DIV, NOT A <p>. It was a paragraph, and a caller with two
   things to say handed it two paragraphs: a paragraph inside a paragraph, which
   a browser will not build. On a page drawn by a server that is a different
   page from the one React drew — React error #418 on every partner 404, and a
   redraw that dropped the reader's light or dark choice. components.css gives
   the div what the paragraph had, so plain words look exactly as they did. */
export function EmptyState({
  icon = 'stack',
  title,
  titleAs = 'div',
  children,
  action,
}: {
  icon?: string;
  title: string;
  /**
   * What the title is drawn as. A div by default, which is what a state inside
   * a screen wants — the screen has its own heading. A page whose whole content
   * is this state (a wrong address, a crash) passes a heading level, so it has
   * one to land on. Same class either way, so nothing on screen changes.
   */
  titleAs?: 'div' | 'h1' | 'h2' | 'h3';
  children: ReactNode;
  action?: ReactNode;
}) {
  const Title = titleAs;
  return (
    <div className="empty-state">
      <Icon name={icon} className="empty-state-icon" />
      <Title className="empty-state-title">{title}</Title>
      <div className="empty-state-body">{children}</div>
      {action}
    </div>
  );
}

/* ── StatusDot ──────────────────────────────────────────────────────────────
   Nocturne ships no red or green. State is carried by the ramps PLUS a written
   label — which is also why the label is required rather than optional here:
   colour alone is not a status. */
export function StatusDot({
  tone = 'ok',
  label,
  live = false,
}: {
  tone?: 'ok' | 'quiet' | 'attention';
  label: string;
  live?: boolean;
}) {
  const cls = ['status-dot', tone !== 'ok' && tone, live && 'live'].filter(Boolean).join(' ');
  return (
    <>
      <span className={cls} />
      <span className="sr-only">{label}</span>
    </>
  );
}
